/**
 * ReadingGuide — quiet side-rail navigator for the Manifesto.
 *
 * A vertical column of small horizontal lines, fixed to the right edge.
 * Each line corresponds to a section in the Manifesto. Behavior:
 *
 *   - The line of the section currently in view is highlighted in accent
 *     and slightly longer.
 *   - Hovering any line reveals its label inline (italic, paper-toned).
 *   - Click smooth-scrolls to that section.
 *   - Hidden on mobile (the page is short enough to scroll naturally there);
 *     visible from `lg` breakpoint upward.
 *
 * Aesthetic doctrine:
 *   - Restrained. No dots, no numbers, no chrome. Just lines that
 *     lengthen into recognition.
 *   - The current-section detection uses IntersectionObserver — no
 *     scroll-listener thrash, no layout shift.
 *   - Aria attributes make it a proper navigation landmark for screen
 *     readers; aria-current marks the active section.
 *
 * Section ids are coupled to apps/site/src/App.tsx — keep in sync if
 * sections are added or renamed.
 */

import { useEffect, useState } from 'react';

interface SectionEntry {
  id: string;
  label: string;
}

const SECTIONS: SectionEntry[] = [
  { id: 'hero', label: 'Manifesto' },
  { id: 'definition', label: 'I  ·  The Definition' },
  { id: 'philosophy', label: 'II  ·  The Expanded Philosophy' },
  { id: 'intelligence', label: 'III  ·  The Intelligence' },
  { id: 'states', label: 'IV  ·  The Five States' },
  { id: 'function', label: 'V  ·  The Function' },
  { id: 'creed', label: 'VI  ·  The Creed' },
  { id: 'universal', label: 'VII  ·  The Universal Scope' },
  { id: 'origin', label: 'VIII  ·  The Origin' },
  { id: 'closing', label: 'Closing' },
  { id: 'evolution', label: 'The Evolution' },
];

export function ReadingGuide() {
  const [activeId, setActiveId] = useState<string>('hero');
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    // Track which section is currently the dominant one in view.
    // rootMargin biases toward sections whose top has crossed into the
    // upper third of the viewport — natural "you're reading this" position.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        // Pick the entry whose top is closest to the viewport's reading band.
        const dominant = visible.reduce((a, b) =>
          Math.abs(a.boundingClientRect.top) < Math.abs(b.boundingClientRect.top) ? a : b
        );
        if (dominant.target.id) {
          setActiveId(dominant.target.id);
        }
      },
      {
        rootMargin: '-15% 0px -55% 0px',
        threshold: 0,
      }
    );

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  function jumpTo(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  return (
    <nav
      aria-label="Manifesto sections"
      className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-10 flex-col gap-3.5"
    >
      {SECTIONS.map((section) => {
        const isActive = activeId === section.id;
        const isHovered = hoveredId === section.id;
        const showLabel = isActive || isHovered;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => jumpTo(section.id)}
            onMouseEnter={() => setHoveredId(section.id)}
            onMouseLeave={() => setHoveredId(null)}
            onFocus={() => setHoveredId(section.id)}
            onBlur={() => setHoveredId(null)}
            aria-label={`Jump to ${section.label}`}
            aria-current={isActive ? 'true' : undefined}
            className="flex items-center justify-end gap-3 cursor-pointer group/guide focus:outline-none"
          >
            <span
              className={`text-[0.7rem] font-display italic tracking-wide whitespace-nowrap transition-opacity duration-200 select-none ${
                showLabel ? 'opacity-70 text-ink' : 'opacity-0'
              }`}
            >
              {section.label}
            </span>
            <span
              className={`block transition-all duration-200 ${
                isActive
                  ? 'w-7 h-px bg-accent'
                  : isHovered
                    ? 'w-5 h-px bg-ink/60'
                    : 'w-3 h-px bg-ink/25'
              }`}
            />
          </button>
        );
      })}
    </nav>
  );
}
